import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, FormHelperText, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "react-phone-input-2/lib/bootstrap.css";

import PhoneInput from "react-phone-input-2";
import dayjs from "dayjs";
import {
  useAddUserMutation,
  useGetUsersByIdQuery,
  useUpdateUsersByIdMutation,
} from "../../services/user";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showError, showToast } from "../../constants/toasts";
import usePermission from "../../hooks/userAccess";

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const { isAdd } = usePermission({
    label: "Manage Users",
    page_permission: "isAdd",
  });

  const [AddUser] = useAddUserMutation();
  const [updateUser] = useUpdateUsersByIdMutation();

  const { data: userRes, isLoading: isUserLoading } = useGetUsersByIdQuery(
    id ? { id } : ({ id: "" } as any),
    { skip: !id }
  );

  const isAddUser = location.pathname === "/manage-users/add";

  const [user, setUser] = useState<any>(null);
  const maxDob = dayjs().subtract(13, "year");

  // Update user state when API data is received
  useEffect(() => {
    if (userRes?.statusCode === 200 && userRes?.data && !isAddUser) {
      setUser(userRes.data);
    }
  }, [userRes, isAddUser]);

  const intitalValues = useMemo(() => {
    return {
      fullName: user?.fullName || "",
      email: user?.email || "",
      phoneNo: user?.phoneNo || "",
      dialCode: user?.dialCode || "+971", // Default dial code set to UAE
      // memberAccount: user?.memberAccount || "",
      dateOfBirth: user?.dob ? dayjs(user?.dob) : null,
      nationality: user?.nationality || "",
    };
  }, [user]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: intitalValues,
    validationSchema: Yup.object({
      fullName: Yup.string()
        .required("Full name is required")
        .test(
          "no-blank-spaces",
          "Full name cannot be only blank spaces",
          (value) => {
            return value ? value.trim().length > 0 : false;
          }
        )
        .test(
          "min-length",
          "Full name must be at least 3 characters",
          (value) => {
            return value ? value.trim().length >= 3 : false;
          }
        ),
      // memberAccount: Yup.string()
      //   .required("Member Account is required"),

      email: Yup.string()
        .required("Email is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        ),

      dialCode: Yup.string()
        .required("Dial code is required")
        .matches(/^\+?[0-9]+$/, "Enter a valid dial code"),

      phoneNo: Yup.string()
        .required("Phone number is required")
        .min(9, "Phone number must be at least 9 digits")
        .matches(/^[0-9]+$/, "Phone number must only contain digits")
        .test(
          "not-all-same-digits",
          "Phone number cannot be all zeros or all the same digits",
          (value) => {
            if (!value) return true; // Let required validation handle empty values
            // Check if all digits are the same (including all zeros like 000000000000)
            const firstDigit = value[0];
            return !value.split("").every((digit) => digit === firstDigit);
          }
        ),

      dateOfBirth: Yup.mixed()
        .nullable()
        .required("Date of birth is required")
        .test("is-valid-date", "Date of birth is required", (value) => {
          if (!value) return false;
          const dateValue = value as
            | string
            | number
            | Date
            | dayjs.Dayjs
            | null
            | undefined;
          return dayjs(dateValue).isValid();
        })
        .test("max-date", "User must be at least 13 years old", (value) => {
          if (!value) return false;
          const dateValue = value as
            | string
            | number
            | Date
            | dayjs.Dayjs
            | null
            | undefined;
          if (!dayjs(dateValue).isValid()) return false;
          return (
            dayjs(dateValue).isBefore(maxDob) || dayjs(dateValue).isSame(maxDob)
          );
        }),
    }),
    onSubmit: async (values) => {
      // Format phone number to remove leading zeros if present
      const formattedPhoneNo = values?.phoneNo
        ? String(values.phoneNo).replace(/^0+/, "")
        : "";

      const body: any = {
        fullName: values?.fullName?.trim(),
        email: values?.email,
        phoneNo: formattedPhoneNo,
        dialCode: (values?.dialCode || "").startsWith("+")
          ? values.dialCode
          : `+${values?.dialCode || ""}`,
        dateOfBirth: values?.dateOfBirth
          ? dayjs(values.dateOfBirth).format("YYYY-MM-DDT00:00:00.000[Z]")
          : null,
        // nationality: "India",
        // memberAccount: values?.memberAccount,
      };
      try {
        let response;
        if (id) {
          response = await updateUser({ body, id }).unwrap();
        } else {
          response = await AddUser(body).unwrap();
        }

        if (response?.statusCode === 200) {
          showToast(`User ${id ? "updated" : "added"} successfully`);
          navigate(-1);
        }
      } catch (err: any) {
        showError(err?.data?.message || "Something went wrong");
      }
    },
  });

  const handlePhoneChange = (phoneValue: any, country: any) => {
    const phoneWithoutCode = phoneValue?.replace(country.dialCode, "");
    formik.setFieldValue("phoneNo", phoneWithoutCode);
    const codeWithPlus = country?.dialCode?.toString().startsWith("+")
      ? country.dialCode
      : `+${country.dialCode}`;
    formik.setFieldValue("dialCode", codeWithPlus);
  };

  // Show loading state while fetching user data for edit mode
  if (!isAddUser && isUserLoading) {
    return (
      <div className="main_title">
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>{isAddUser ? "Add " : "Edit "} User</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-users")}>
            Manage Users
          </button>{" "}
          - {isAddUser ? "Add " : "Edit "} User
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_33">
              <label htmlFor="fullName">Full Name</label>
              <TextField
                name="fullName"
                id="fullName"
                placeholder="Name"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                hiddenLabel
                helperText={
                  formik.touched.fullName && (formik.errors.fullName as any)
                }
                error={
                  formik.touched.fullName && Boolean(formik.errors.fullName)
                }
                fullWidth
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="email">Email</label>
              <TextField
                id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.email && (formik.errors.email as any)
                }
                error={formik.touched.email && Boolean(formik.errors.email)}
                type="email"
                placeholder="Email"
                hiddenLabel
                fullWidth
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="phone">Phone Number</label>
              <PhoneInput
                country={"ae"}
                placeholder="Enter Phone Number"
                value={formik.values.dialCode + formik.values.phoneNo}
                onChange={(phoneNo, country) =>
                  handlePhoneChange(phoneNo, country)
                }
                inputStyle={{
                  width: "100%",
                  border:
                    formik.touched.phoneNo && formik.errors.phoneNo
                      ? "1px solid #d32f2f"
                      : "1px solid #ccc",
                }}
              />
              {formik.touched.phoneNo && formik.errors.phoneNo && (
                <FormHelperText error>
                  {formik.errors.phoneNo as any}
                </FormHelperText>
              )}
            </div>

            <div className="control_group w_33">
              <label htmlFor="dateOfBirth">DOB</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formik.values.dateOfBirth}
                  onChange={(value) => {
                    formik.setFieldValue(
                      "dateOfBirth",
                      value ? dayjs(value) : null
                    );
                  }}
                  maxDate={maxDob}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      id: "dateOfBirth",
                      name: "dateOfBirth",
                      fullWidth: true,
                      hiddenLabel: true,
                      variant: "outlined",
                      onBlur: () =>
                        formik.setFieldTouched("dateOfBirth", true, true),
                      error:
                        formik.touched.dateOfBirth &&
                        Boolean(formik.errors.dateOfBirth),
                      helperText:
                        formik.touched.dateOfBirth && formik.errors.dateOfBirth,
                    },
                    actionBar: {
                      actions: ["clear"],
                    },
                  }}
                  disableFuture
                  desktopModeMediaQuery="(min-width:0px)"
                />
              </LocalizationProvider>
            </div>

            {/* <div className="control_group w_33">
              <label htmlFor="memberAccount">Member Account</label>
              <TextField
                id="memberAccount"
                name="memberAccount"
                placeholder="Member Account"
                value={formik.values.memberAccount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                hiddenLabel
                helperText={
                  formik.touched.memberAccount &&
                  (formik.errors.memberAccount as any)
                }
                error={
                  formik.touched.memberAccount &&
                  Boolean(formik.errors.memberAccount)
                }
                fullWidth
              />
            </div> */}
          </div>

          {isAdd && (
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                type="submit"
                disabled={formik.isSubmitting || (!isAddUser && isUserLoading)}
              >
                {formik.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </form>
      </div>
    </>
  );
};

export default EditUser;
